import React, { useEffect, useState } from "react";
import { confirmPopup } from "primereact/confirmpopup";
import { classNames } from "primereact/utils";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AppDialog from "../../Dialogs/AppDialog/AppDialog";
import { DashlyTextInput, LabelInputField } from "../../inputs";
import {
    ISavedFilter,
    SavedFilterService,
    SAVED_FILTER_VERSION,
    logSavedFilterError,
} from "../../../Services/SavedFilterService";
import { SavedFilterScope } from "./SavedFilterScopeContext";
import "./SavedFilterDialogs.scss";

interface SavedFilterDialogsProps {
    scope: SavedFilterScope | null;
    saveOpen: boolean;
    manageOpen: boolean;
    onCloseSave: () => void;
    onCloseManage: () => void;
}

const SavedFilterDialogs: React.FC<SavedFilterDialogsProps> = ({
    scope,
    saveOpen,
    manageOpen,
    onCloseSave,
    onCloseManage,
}) => {
    const { t } = useTranslation();
    const saveMethods = useForm<{ name: string }>({ defaultValues: { name: "" } });
    const saveName = saveMethods.watch("name");
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<ISavedFilter[]>([]);
    const [loading, setLoading] = useState(false);
    const [busyUuid, setBusyUuid] = useState<string | null>(null);
    const [renameUuid, setRenameUuid] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [viewUuid, setViewUuid] = useState<string | null>(null);
    const [saveRows, setSaveRows] = useState<ISavedFilter[]>([]);

    useEffect(() => {
        if (!saveOpen || !scope) {
            return;
        }
        saveMethods.reset({ name: "" });
        let cancelled = false;
        new SavedFilterService().list(scope.location, scope.locationIdentifier)
            .then((response) => {
                if (!cancelled) {
                    const payload = response.data?.data;
                    setSaveRows(Array.isArray(payload) ? payload : []);
                }
            })
            .catch((error) => {
                logSavedFilterError("list", error);
                if (!cancelled) {
                    setSaveRows([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [saveMethods, saveOpen, scope]);

    useEffect(() => {
        if (!manageOpen || !scope) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        setRenameUuid(null);
        new SavedFilterService().list(scope.location, scope.locationIdentifier)
            .then((response) => {
                if (!cancelled) {
                    const payload = response.data?.data;
                    const next = Array.isArray(payload) ? payload : [];
                    setRows(next);
                    setViewUuid(next.find((row) => row.defaultFilter)?.uuid ?? null);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    logSavedFilterError("list", error);
                    setRows([]);
                    toast.error(t("savedFilter.toast.failed"));
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [manageOpen, scope, t]);

    const reload = () => {
        if (!scope) {
            return;
        }
        setLoading(true);
        new SavedFilterService().list(scope.location, scope.locationIdentifier)
            .then((response) => {
                const payload = response.data?.data;
                const next = Array.isArray(payload) ? payload : [];
                setRows(next);
            })
            .catch((error) => {
                logSavedFilterError("list", error);
                toast.error(t("savedFilter.toast.failed"));
            })
            .finally(() => setLoading(false));
    };

    const currentConfiguration = () => ({
        version: SAVED_FILTER_VERSION,
        values: scope?.getValues() ?? {},
    });

    const saveCurrent = () => {
        if (!scope) {
            return;
        }
        const trimmed = saveMethods.getValues("name").trim();
        if (!trimmed) {
            return;
        }
        const existing = saveRows.find((row) => row.name === trimmed);
        setSaving(true);
        const request = existing
            ? new SavedFilterService().update(existing.uuid, { configuration: currentConfiguration() })
            : new SavedFilterService().create({
                name: trimmed,
                location: scope.location,
                locationIdentifier: scope.locationIdentifier,
                configuration: currentConfiguration(),
                defaultFilter: true,
            });
        request
            .then(() => {
                toast.success(t(existing ? "savedFilter.toast.overwritten" : "savedFilter.toast.saved"));
                onCloseSave();
            })
            .catch((error) => {
                logSavedFilterError(existing ? "overwrite" : "save", error);
                toast.error(t("savedFilter.toast.failed"));
            })
            .finally(() => setSaving(false));
    };

    const overwrite = (row: ISavedFilter, event: React.MouseEvent<HTMLButtonElement>) => {
        if (!scope) {
            return;
        }
        confirmPopup({
            target: event.currentTarget,
            message: t("savedFilter.confirm.overwrite", { name: row.name }),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("savedFilter.action.overwrite"),
            rejectLabel: t("common.action.cancel"),
            accept: () => {
                setBusyUuid(row.uuid);
                new SavedFilterService().update(row.uuid, { configuration: currentConfiguration() })
                    .then(() => {
                        toast.success(t("savedFilter.toast.overwritten"));
                        reload();
                    })
                    .catch((error) => {
                        logSavedFilterError("overwrite", error);
                        toast.error(t("savedFilter.toast.failed"));
                    })
                    .finally(() => setBusyUuid(null));
            },
        });
    };

    const rename = (row: ISavedFilter) => {
        const trimmed = renameValue.trim();
        if (!trimmed || trimmed === row.name) {
            setRenameUuid(null);
            return;
        }
        setBusyUuid(row.uuid);
        new SavedFilterService().update(row.uuid, { name: trimmed })
            .then(() => {
                toast.success(t("savedFilter.toast.renamed"));
                setRenameUuid(null);
                reload();
            })
            .catch((error) => {
                logSavedFilterError("rename", error);
                toast.error(t("savedFilter.toast.failed"));
            })
            .finally(() => setBusyUuid(null));
    };

    const makeDefault = (row: ISavedFilter) => {
        setBusyUuid(row.uuid);
        new SavedFilterService().setDefault(row.uuid)
            .then(() => {
                toast.success(t("savedFilter.toast.defaultSet"));
                setViewUuid(row.uuid);
                reload();
            })
            .catch((error) => {
                logSavedFilterError("set default", error);
                toast.error(t("savedFilter.toast.failed"));
            })
            .finally(() => setBusyUuid(null));
    };

    const clearDefault = (row: ISavedFilter) => {
        setBusyUuid(row.uuid);
        new SavedFilterService().clearDefault(row.uuid)
            .then(() => {
                toast.success(t("savedFilter.toast.defaultCleared"));
                setViewUuid(row.uuid);
                reload();
            })
            .catch((error) => {
                logSavedFilterError("clear default", error);
                toast.error(t("savedFilter.toast.failed"));
            })
            .finally(() => setBusyUuid(null));
    };

    const remove = (row: ISavedFilter, event: React.MouseEvent<HTMLButtonElement>) => {
        confirmPopup({
            target: event.currentTarget,
            message: t("savedFilter.confirm.delete", { name: row.name }),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: t("savedFilter.action.delete"),
            rejectLabel: t("common.action.cancel"),
            acceptClassName: "p-button-danger",
            accept: () => {
                setBusyUuid(row.uuid);
                new SavedFilterService().remove(row.uuid)
                    .then(() => {
                        toast.success(t("savedFilter.toast.deleted"));
                        reload();
                    })
                    .catch((error) => {
                        logSavedFilterError("delete", error);
                        toast.error(t("savedFilter.toast.failed"));
                    })
                    .finally(() => setBusyUuid(null));
            },
        });
    };

    const existingSaveMatch = saveRows.find((row) => row.name === saveName.trim());
    const viewing = rows.find((row) => row.uuid === viewUuid);
    const viewedFields = viewing && scope
        ? scope.describe(viewing.configuration?.values ?? {})
        : [];

    return (
        <>
            <AppDialog
                header={t("savedFilter.dialog.save.title")}
                visible={saveOpen}
                onHide={onCloseSave}
                style={{ width: "28rem" }}
                footerActions={{
                    cancelLabel: t("common.action.cancel"),
                    onCancel: onCloseSave,
                    cancelDisabled: saving,
                    submitLabel: t(existingSaveMatch ? "savedFilter.dialog.save.overwriteSubmit" : "savedFilter.dialog.save.submit"),
                    onSubmit: saveCurrent,
                    submitDisabled: saving || saveName.trim().length === 0,
                    submitLoading: saving,
                }}
            >
                <FormProvider {...saveMethods}>
                    <LabelInputField
                        name="name"
                        label={t("savedFilter.dialog.save.name")}
                        component={DashlyTextInput}
                        componentProps={{
                            id: "saved-filter-name",
                            autoFocus: true,
                            maxLength: 120,
                            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    saveCurrent();
                                }
                            },
                        }}
                    />
                </FormProvider>
            </AppDialog>

            <AppDialog
                className="saved-filter-manage-dialog"
                header={t("savedFilter.dialog.manage.title")}
                visible={manageOpen}
                onHide={onCloseManage}
                style={{ width: "28rem" }}
            >
                {loading && rows.length === 0 ? (
                    <p className="saved-filter-dialog__empty">{t("common.loading")}</p>
                ) : null}
                {!loading && rows.length === 0 ? (
                    <p className="saved-filter-dialog__empty">{t("savedFilter.dialog.manage.empty")}</p>
                ) : null}
                <ul className="saved-filter-dialog__list">
                    {rows.map((row) => {
                        const renaming = renameUuid === row.uuid;
                        const viewingRow = viewUuid === row.uuid;
                        return (
                            <li
                                key={row.uuid}
                                className={classNames("saved-filter-dialog__row", viewingRow && "is-open")}
                            >
                                <div className="saved-filter-dialog__row-main">
                                    {renaming ? (
                                        <DashlyTextInput
                                            value={renameValue}
                                            maxLength={120}
                                            controlSize="sm"
                                            autoFocus
                                            onChange={(event) => setRenameValue(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.preventDefault();
                                                    rename(row);
                                                }
                                                if (event.key === "Escape") {
                                                    setRenameUuid(null);
                                                }
                                            }}
                                        />
                                    ) : (
                                        <span className="saved-filter-dialog__name">{row.name}</span>
                                    )}
                                    {!renaming && row.defaultFilter ? (
                                        <span className="saved-filter-dialog__badge">{t("savedFilter.label.default")}</span>
                                    ) : null}
                                    <div className="saved-filter-dialog__actions">
                                        {renaming ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="saved-filter-dialog__icon-btn"
                                                    disabled={busyUuid === row.uuid || renameValue.trim().length === 0}
                                                    aria-label={t("savedFilter.action.renameSave")}
                                                    title={t("savedFilter.action.renameSave")}
                                                    onClick={() => rename(row)}
                                                >
                                                    <i className="pi pi-check" aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="saved-filter-dialog__icon-btn"
                                                    disabled={busyUuid === row.uuid}
                                                    aria-label={t("common.action.cancel")}
                                                    title={t("common.action.cancel")}
                                                    onClick={() => setRenameUuid(null)}
                                                >
                                                    <i className="pi pi-times" aria-hidden />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                {row.defaultFilter ? (
                                                    <button
                                                        type="button"
                                                        className="saved-filter-dialog__use"
                                                        disabled={busyUuid === row.uuid}
                                                        onClick={() => clearDefault(row)}
                                                    >
                                                        {t("savedFilter.action.clearDefault")}
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="saved-filter-dialog__use"
                                                        disabled={busyUuid === row.uuid}
                                                        onClick={() => makeDefault(row)}
                                                    >
                                                        {t("savedFilter.action.useDefault")}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="saved-filter-dialog__icon-btn"
                                                    disabled={busyUuid === row.uuid}
                                                    aria-label={t("savedFilter.action.overwrite", { defaultValue: "Overwrite with current filters" })}
                                                    title={t("savedFilter.action.overwrite", { defaultValue: "Overwrite with current filters" })}
                                                    onClick={(event) => overwrite(row, event)}
                                                >
                                                    <i className="pi pi-refresh" aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="saved-filter-dialog__icon-btn"
                                                    disabled={busyUuid === row.uuid}
                                                    aria-label={t("savedFilter.action.rename")}
                                                    title={t("savedFilter.action.rename")}
                                                    onClick={() => {
                                                        setRenameUuid(row.uuid);
                                                        setRenameValue(row.name);
                                                        setViewUuid(null);
                                                    }}
                                                >
                                                    <i className="pi pi-pencil" aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={classNames(
                                                        "saved-filter-dialog__icon-btn",
                                                        viewingRow && "is-active",
                                                    )}
                                                    disabled={busyUuid === row.uuid}
                                                    aria-label={t("savedFilter.action.view")}
                                                    aria-expanded={viewingRow}
                                                    title={t("savedFilter.action.view")}
                                                    onClick={() => setViewUuid((current) => (current === row.uuid ? null : row.uuid))}
                                                >
                                                    <i className={viewingRow ? "pi pi-eye-slash" : "pi pi-eye"} aria-hidden />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="saved-filter-dialog__icon-btn is-danger"
                                                    disabled={busyUuid === row.uuid}
                                                    aria-label={t("savedFilter.action.delete")}
                                                    title={t("savedFilter.action.delete")}
                                                    onClick={(event) => remove(row, event)}
                                                >
                                                    <i className="pi pi-trash" aria-hidden />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {viewingRow ? (
                                    <dl className="saved-filter-dialog__details">
                                        {viewedFields.length === 0 ? (
                                            <div className="saved-filter-dialog__empty">{t("savedFilter.dialog.manage.noValues")}</div>
                                        ) : viewedFields.map((field) => (
                                            <div key={field.label} className="saved-filter-dialog__detail">
                                                <dt>{field.label}</dt>
                                                <dd>{field.value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            </AppDialog>
        </>
    );
};

export default SavedFilterDialogs;
